import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  estadoMotor, userIdDeCuenta, parsearFormato, listarFormatos,
  obtenerFormato, guardarFormato, borrarFormato, generarMinuta,
  listarPlantillasV1, importarPlantillasV1, descargarComoWord, loQuePuedo
} from '../../services/minutasV2Service';
import { FUENTE, useTema } from './tema';
import { useConfirmacion } from './Confirmacion';
import { normalizarTranscripcion } from '../../services/transcripcion';

/**
 * GeneradorMinutaV2
 *
 * La v2 en una pantalla: se elige el FORMATO (.docx), se aporta la TRANSCRIPCIÓN,
 * y el motor devuelve la minuta.
 *
 * El formato puede venir de tres sitios: uno guardado, uno recién subido, o
 * ninguno (minuta de estructura libre).
 *
 * El componente es autocontenido a propósito: no depende de ClipchampViewer ni de
 * su estado, para poder montarse en cualquier punto de la app mientras se desmonta
 * el monolito por fases.
 */

// Esta pantalla nació con paleta clara fija, mientras el resto de la v2 era
// oscura. Ahora las tres comparten la del tema, así que cambiar de modo no
// deja una pantalla descolgada.
const estilos = (C) => ({
  wrap: {
    maxWidth: 1100, margin: '0 auto', padding: '24px 20px 64px', fontFamily: FUENTE,
    background: C.fondo, color: C.texto, minHeight: '100vh', boxSizing: 'border-box'
  },
  h1: { fontSize: 24, fontWeight: 700, color: C.texto, margin: '0 0 4px' },
  sub: { fontSize: 14, color: C.suave, margin: '0 0 24px' },
  card: {
    background: C.panel, border: `1px solid ${C.borde}`, borderRadius: 10,
    padding: 20, marginBottom: 16, boxShadow: C.sombra
  },
  cardTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, fontWeight: 600, color: C.texto, margin: '0 0 14px' },
  step: {
    width: 22, height: 22, borderRadius: '50%', background: C.acento, color: C.acentoTexto,
    fontSize: 12, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  tabs: { display: 'flex', gap: 6, marginBottom: 16, borderBottom: `1px solid ${C.borde}` },
  tab: (activo) => ({
    padding: '8px 14px', fontSize: 14, fontWeight: activo ? 600 : 400, cursor: 'pointer',
    fontFamily: FUENTE,
    color: activo ? C.texto : C.suave, background: 'none', border: 'none',
    borderBottom: `2px solid ${activo ? C.acento : 'transparent'}`, marginBottom: -1
  }),
  fila: (sel) => ({
    display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', marginBottom: 8,
    border: `1px solid ${sel ? C.acento : C.borde}`, borderRadius: 8,
    background: sel ? C.acentoSuave : C.panel, cursor: 'pointer'
  }),
  drop: (activo) => ({
    border: `2px dashed ${activo ? C.acento : C.borde}`,
    background: activo ? C.acentoSuave : C.panelAlto,
    borderRadius: 8, padding: 28, textAlign: 'center', cursor: 'pointer', transition: 'all .15s'
  }),
  textarea: {
    width: '100%', minHeight: 180, padding: 12, borderRadius: 8,
    border: `1px solid ${C.borde}`, background: C.panelAlto, color: C.texto,
    fontFamily: 'ui-monospace, Consolas, monospace', fontSize: 13, lineHeight: 1.5, resize: 'vertical', boxSizing: 'border-box'
  },
  input: {
    width: '100%', padding: '9px 12px', borderRadius: 8, border: `1px solid ${C.borde}`,
    background: C.panelAlto, color: C.texto, fontSize: 14, fontFamily: FUENTE, boxSizing: 'border-box'
  },
  btn: (disabled) => ({
    padding: '11px 22px', borderRadius: 8, border: 'none', fontSize: 15, fontWeight: 600, fontFamily: FUENTE,
    background: disabled ? C.tenue : C.acento, color: C.acentoTexto,
    cursor: disabled ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8
  }),
  btnSec: {
    background: 'none', border: `1px solid ${C.borde}`, borderRadius: 6, padding: '6px 12px',
    cursor: 'pointer', fontSize: 13, fontFamily: FUENTE, color: C.texto
  },
  chip: {
    display: 'inline-block', padding: '3px 9px', borderRadius: 12, background: C.acentoSuave,
    color: C.acentoClaro, fontSize: 12, margin: '0 5px 5px 0', fontFamily: 'ui-monospace, monospace'
  },
  etiqueta: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
    background: C.panelAlto, color: C.exito, fontSize: 11, fontWeight: 600
  },
  meta: { display: 'flex', flexWrap: 'wrap', gap: 18, fontSize: 13, color: C.suave, marginTop: 12 },
  preview: {
    border: `1px solid ${C.borde}`, borderRadius: 8, padding: 22,
    background: C.panel, color: C.texto, maxHeight: 620, overflowY: 'auto'
  }
});

const GeneradorMinutaV2 = ({ reunion = null, archivoInicial = null, onVolver = null }) => {
  const { C, aviso } = useTema();
  const S = useMemo(() => estilos(C), [C]);

  const { accounts } = useMsal();
  const userId = userIdDeCuenta(accounts && accounts[0]);

  const [motor, setMotor] = useState(null);

  // Los avisos de lo que no se deshace se pintan dentro de la aplicación, no
  // con el cuadro del navegador. Se declara arriba porque `confirmar` entra en
  // las dependencias de un `useCallback` de más abajo, y esas se evalúan al
  // crearlo: declararlo después daría un error de zona muerta en cada pintado.
  const [dialogo, confirmar] = useConfirmacion();

  // Formato: pestaña activa, guardados, y el recién subido
  const [pestana, setPestana] = useState('guardados'); // guardados | subir | ninguno

  // ── Subir una plantilla lo lleva un grupo reducido ───────────────────
  //
  // Tambien aqui, y no solo en la configuracion: este es el otro sitio desde
  // el que se puede meter un .docx, y dejarlo abierto habria convertido el
  // permiso en un adorno de una pantalla.
  //
  // `null` mientras no se sabe. La pestaña no se pinta hasta que haya
  // respuesta: aparecer y desaparecer medio segundo despues se lee como un
  // fallo.
  const [puedo, setPuedo] = useState(null);

  useEffect(() => {
    let vigente = true;
    loQuePuedo()
      .then(p => { if (vigente) setPuedo(p); })
      // Sin respuesta, se da por supuesto que no. Al reves se enseñaria la
      // pestaña a todo el mundo cada vez que fallara la red.
      .catch(() => { if (vigente) setPuedo({ puedePlantillas: false }); });
    return () => { vigente = false; };
  }, []);

  // Y si estaba en «Subir .docx» cuando llega la respuesta, se sale de ahi:
  // quedarse en una pestaña que ya no existe deja la pantalla en blanco.
  useEffect(() => {
    if (puedo && !puedo.puedePlantillas && pestana === 'subir') setPestana('guardados');
  }, [puedo, pestana]);
  const [guardados, setGuardados] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [formatIdSel, setFormatIdSel] = useState(null);
  const [pendientesV1, setPendientesV1] = useState([]);
  const [bdCaida, setBdCaida] = useState(false);
  const [importando, setImportando] = useState(false);
  const [formatoSubido, setFormatoSubido] = useState(null);
  const [cargandoFormato, setCargandoFormato] = useState(false);
  const [arrastrando, setArrastrando] = useState(false);
  const [archivoSubido, setArchivoSubido] = useState(null);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [guardandoFormato, setGuardandoFormato] = useState(false);

  const [transcripcion, setTranscripcion] = useState('');
  const [indicaciones, setIndicaciones] = useState('');

  const [exportando, setExportando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [nota, setNota] = useState(null);

  const inputFormato = useRef(null);
  const inputTranscripcion = useRef(null);

  useEffect(() => {
    estadoMotor()
      .then(setMotor)
      .catch(e => setMotor({ ready: false, mensaje: e.mensajeUsuario || e.message }));
  }, []);

  // Si se llegó aquí arrastrando un .docx desde el inicio, se analiza solo.
  useEffect(() => {
    if (!archivoInicial) return;
    setPestana('subir');
    subirFormato(archivoInicial);
    // subirFormato es estable (useCallback sin dependencias cambiantes).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [archivoInicial]);

  const recargarGuardados = useCallback(async () => {
    if (!userId) return;
    setCargandoLista(true);
    try {
      setGuardados(await listarFormatos(userId));
      setBdCaida(false);
    } catch (e) {
      // Guardar formatos es una comodidad, no un requisito: subir un .docx y
      // generar con él no toca la base de datos. Si la base falla, se degrada
      // a ese camino en vez de bloquear la pantalla.
      const esFalloDeBd = ['DB_CONNECTION_FAILED', 'DB_NOT_CONFIGURED', 'FORMATS_TABLE_MISSING'].includes(e.code);
      setBdCaida(esFalloDeBd);
      setNota(esFalloDeBd
        ? e.mensajeUsuario
        : `No se pudieron cargar los formatos guardados: ${e.mensajeUsuario || e.message}`);
      setGuardados([]);
      if (esFalloDeBd) setPestana('subir');
    } finally {
      setCargandoLista(false);
    }
  }, [userId]);

  /** Plantillas de v1 que todavía no se han traído a v2. */
  const recargarPendientesV1 = useCallback(async () => {
    if (!userId) return;
    try {
      const plantillas = await listarPlantillasV1(userId);
      setPendientesV1(plantillas.filter(p => p.tieneHtml && !p.yaImportada));
    } catch {
      // Si v1 no está disponible simplemente no se ofrece importar.
      setPendientesV1([]);
    }
  }, [userId]);

  useEffect(() => {
    recargarGuardados();
    recargarPendientesV1();
  }, [recargarGuardados, recargarPendientesV1]);

  const importarDeV1 = useCallback(async () => {
    setError(null);
    setImportando(true);
    try {
      const r = await importarPlantillasV1(userId);
      await recargarGuardados();
      await recargarPendientesV1();

      const partes = [`${r.totalImported} plantilla(s) importada(s) desde v1.`];
      if (r.totalSkipped > 0) {
        partes.push(`Omitidas ${r.totalSkipped}: ${r.skipped.map(s => `${s.name} (${s.motivo})`).join('; ')}`);
      }
      setNota(partes.join(' '));
      setPestana('guardados');
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    } finally {
      setImportando(false);
    }
  }, [userId, recargarGuardados, recargarPendientesV1]);

  // Si no hay nada guardado, la pestaña útil es la de subir. Salvo que haya
  // plantillas de v1 por importar: entonces conviene quedarse aquí para verlas.
  useEffect(() => {
    if (!cargandoLista && guardados.length === 0 && pendientesV1.length === 0 && pestana === 'guardados') {
      setPestana('subir');
    }
  }, [cargandoLista, guardados.length, pendientesV1.length, pestana]);

  const subirFormato = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setCargandoFormato(true);
    try {
      const parseado = await parsearFormato(file);
      setFormatoSubido(parseado);
      setArchivoSubido(file);
      setNombreNuevo((file.name || '').replace(/\.docx$/i, ''));
      setFormatIdSel(null);
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
      setFormatoSubido(null);
      setArchivoSubido(null);
    } finally {
      setCargandoFormato(false);
    }
  }, []);

  const guardarParaReutilizar = useCallback(async () => {
    if (!archivoSubido || !userId) return;
    setError(null);
    setGuardandoFormato(true);
    try {
      const guardado = await guardarFormato({
        file: archivoSubido,
        userId,
        nombre: nombreNuevo.trim() || undefined,
        indicaciones: indicaciones.trim() || null
      });
      await recargarGuardados();
      setFormatIdSel(guardado.formatId);
      setFormatoSubido(null);
      setArchivoSubido(null);
      setPestana('guardados');
      setNota(`Formato "${guardado.name}" guardado.`);
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    } finally {
      setGuardandoFormato(false);
    }
  }, [archivoSubido, userId, nombreNuevo, indicaciones, recargarGuardados]);

  const eliminar = useCallback(async (formatId, nombre) => {
    if (!await confirmar({
      titulo: 'Borrar este formato',
      mensaje: `Se borrará «${nombre}» y el .docx que subiste con él.\n\n` +
        'Las minutas ya generadas con este formato se conservan, pero no se podrán volver a ' +
        'generar ni corregir sobre él sin subirlo otra vez.',
      aceptar: 'Borrarlo',
      peligro: true
    })) return;
    setError(null);
    try {
      await borrarFormato(formatId, userId);
      if (formatIdSel === formatId) setFormatIdSel(null);
      await recargarGuardados();
      setNota(`Formato "${nombre}" borrado.`);
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    }
  }, [userId, formatIdSel, recargarGuardados, confirmar]);

  /**
   * Carga un archivo en la caja de la transcripción.
   *
   * Lo que se pone en la caja es la conversación LEÍDA, no el archivo tal cual.
   * Un .vtt son dos tercios de cabecera, números de cue y flechas de tiempo:
   * volcarlo crudo llenaba la caja de algo que no se puede revisar de un
   * vistazo, y le mandaba al modelo la mitad de los tokens en marcas de formato.
   *
   * Si el archivo no se reconoce como transcripción se pone tal cual, que es lo
   * que había antes: mejor eso que una caja vacía sin explicación.
   */
  const cargarTranscripcion = useCallback((file) => {
    if (!file) return;
    const lector = new FileReader();
    lector.onload = () => {
      const texto = String(lector.result || '');
      const leida = normalizarTranscripcion(texto);
      setTranscripcion(leida && leida.texto ? leida.texto : texto);
    };
    lector.onerror = () => setError('No se pudo leer el archivo de transcripción');
    lector.readAsText(file);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setArrastrando(false);
    subirFormato(e.dataTransfer.files && e.dataTransfer.files[0]);
  }, [subirFormato]);

  const generar = useCallback(async () => {
    setError(null);
    setNota(null);
    setResultado(null);
    setGenerando(true);
    try {
      const usaGuardado = pestana === 'guardados' && formatIdSel != null;
      setResultado(await generarMinuta({
        transcripcion,
        formatId: usaGuardado ? formatIdSel : null,
        formato: usaGuardado ? null : (pestana === 'subir' ? formatoSubido : null),
        userId,
        indicaciones: indicaciones.trim() || null
      }));
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    } finally {
      setGenerando(false);
    }
  }, [transcripcion, pestana, formatIdSel, formatoSubido, indicaciones, userId]);

  /** Título por defecto: el del formato elegido, o la fecha. */
  const tituloSugerido = useCallback(() => {
    const delFormato = pestana === 'guardados'
      ? (guardados.find(f => f.formatId === formatIdSel) || {}).name
      : ((formatoSubido && formatoSubido.fileName) || '').replace(/\.docx$/i, '');
    return delFormato || `Minuta ${new Date().toLocaleDateString('es')}`;
  }, [pestana, guardados, formatIdSel, formatoSubido]);

  /** La minuta como documento Word: se genera en el backend. */
  const descargarWord = useCallback(async () => {
    if (!resultado) return;
    setError(null);
    setExportando(true);
    try {
      await descargarComoWord({ html: resultado.html, titulo: tituloSugerido() });
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    } finally {
      setExportando(false);
    }
  }, [resultado, tituloSugerido]);

  /** Alternativa ligera, sin pasar por el servidor. */
  const descargarHtml = useCallback(() => {
    if (!resultado) return;
    const doc = `<!doctype html><html lang="es"><head><meta charset="utf-8">
<title>${tituloSugerido()}</title><style>body{font-family:Calibri,sans-serif;max-width:800px;margin:40px auto;line-height:1.5}
table{border-collapse:collapse;width:100%}td,th{border:1px solid #999;padding:7px}</style></head>
<body>${resultado.html}</body></html>`;
    const url = URL.createObjectURL(new Blob([doc], { type: 'text/html' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${tituloSugerido()}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [resultado, tituloSugerido]);

  const verFormatoGuardado = useCallback(async (formatId) => {
    try {
      const f = await obtenerFormato(formatId, userId);
      const w = window.open('', '_blank');
      if (w) {
        w.document.write(`<!doctype html><meta charset="utf-8"><title>${f.name}</title>
<body style="font-family:Calibri,sans-serif;max-width:820px;margin:40px auto">${f.html}</body>`);
        w.document.close();
      }
    } catch (e) {
      setError(e.mensajeUsuario || e.message);
    }
  }, [userId]);

  const formatoElegido = pestana === 'guardados'
    ? guardados.find(f => f.formatId === formatIdSel) || null
    : (pestana === 'subir' ? formatoSubido : null);

  const puedeGenerar = transcripcion.trim().length > 0 && !generando && motor && motor.ready;
  const v = resultado && resultado.verificacion;

  return (
    <div style={S.wrap}>
      {dialogo}
      {onVolver && (
        <button
          onClick={onVolver}
          style={{ ...S.btnSec, marginBottom: 16 }}
        >
          ← Volver al inicio
        </button>
      )}

      <h1 style={S.h1}>{reunion ? reunion.titulo : 'Generar minuta'}</h1>
      <p style={S.sub}>
        {reunion
          ? `Reunión del ${new Date(reunion.inicio).toLocaleDateString('es')} · aporta la transcripción y el formato.`
          : 'Elige el formato del acta y aporta la transcripción de la reunión.'}
      </p>

      {motor && !motor.ready && (
        <div style={aviso('error')}><strong>Motor no disponible.</strong> {motor.mensaje}</div>
      )}
      {motor && motor.ready && (
        <div style={aviso('exito')}>
          Motor listo · Azure OpenAI · deployment <strong>{motor.deployment}</strong>
        </div>
      )}
      {!userId && (
        <div style={aviso('atencion')}>
          No se detectó una sesión de Microsoft. Los formatos guardados no estarán disponibles.
        </div>
      )}
      {error && <div style={aviso('error')}>{error}</div>}
      {nota && <div style={aviso('info')}>{nota}</div>}

      {/* ── 1. Formato ── */}
      <div style={S.card}>
        <h2 style={S.cardTitle}><span style={S.step}>1</span> Formato del acta</h2>

        <div style={S.tabs}>
          {!bdCaida && (
            <button style={S.tab(pestana === 'guardados')} onClick={() => setPestana('guardados')}>
              Guardados {guardados.length > 0 && `(${guardados.length})`}
            </button>
          )}
          {puedo && puedo.puedePlantillas && (
            <button style={S.tab(pestana === 'subir')} onClick={() => setPestana('subir')}>Subir .docx</button>
          )}
          <button style={S.tab(pestana === 'ninguno')} onClick={() => setPestana('ninguno')}>Sin formato</button>
        </div>

        {pestana === 'guardados' && (
          <>
            {pendientesV1.length > 0 && (
              <div style={{ ...aviso('info'), display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  Tienes {pendientesV1.length} plantilla(s) de la versión anterior sin traer:{' '}
                  {pendientesV1.map(p => p.name).join(', ')}.
                </div>
                <button
                  style={{ ...S.btn(importando), padding: '7px 14px', fontSize: 13 }}
                  onClick={importarDeV1}
                  disabled={importando}
                >
                  {importando ? 'Importando…' : 'Importar'}
                </button>
              </div>
            )}

            {cargandoLista && <div style={{ fontSize: 14, color: '#6b7280' }}>Cargando…</div>}
            {!cargandoLista && guardados.length === 0 && (
              <div style={{ fontSize: 14, color: '#6b7280' }}>
                Todavía no hay formatos guardados. Sube un .docx en la pestaña de al lado.
              </div>
            )}
            {guardados.map(f => (
              <div key={f.formatId} style={S.fila(f.formatId === formatIdSel)} onClick={() => setFormatIdSel(f.formatId)}>
                <input type="radio" checked={f.formatId === formatIdSel} onChange={() => setFormatIdSel(f.formatId)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>
                    {f.name} {f.isPublic && <span style={S.etiqueta}>público</span>}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                    {(f.stats && f.stats.sectionCount) || 0} secciones ·{' '}
                    {(f.stats && f.stats.placeholderCount) || 0} placeholders ·{' '}
                    {f.kind === 'html' ? 'importado de v1' : f.fileName}
                  </div>
                </div>
                <button style={S.btnSec} onClick={(e) => { e.stopPropagation(); verFormatoGuardado(f.formatId); }}>
                  Ver
                </button>
                {f.userId === userId && (
                  <button
                    style={{ ...S.btnSec, color: '#991b1b' }}
                    onClick={(e) => { e.stopPropagation(); eliminar(f.formatId, f.name); }}
                  >
                    Borrar
                  </button>
                )}
              </div>
            ))}
          </>
        )}

        {pestana === 'subir' && (
          <>
            {!formatoSubido ? (
              <div
                style={S.drop(arrastrando)}
                onClick={() => inputFormato.current && inputFormato.current.click()}
                onDragOver={(e) => { e.preventDefault(); setArrastrando(true); }}
                onDragLeave={() => setArrastrando(false)}
                onDrop={onDrop}
              >
                <div style={{ fontSize: 15, color: '#374151', fontWeight: 500 }}>
                  {cargandoFormato ? 'Analizando documento…' : 'Arrastra tu .docx aquí o haz clic para elegirlo'}
                </div>
                <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 6 }}>
                  Se detectan las secciones y los placeholders del documento
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <strong style={{ fontSize: 15 }}>{formatoSubido.fileName}</strong>
                  <button style={S.btnSec} onClick={() => { setFormatoSubido(null); setArchivoSubido(null); }}>
                    Quitar
                  </button>
                </div>

                <div style={S.meta}>
                  <span><strong>{formatoSubido.stats.sectionCount}</strong> secciones</span>
                  <span><strong>{formatoSubido.stats.placeholderCount}</strong> placeholders</span>
                  <span><strong>{formatoSubido.stats.tableCount}</strong> tablas</span>
                  <span>~<strong>{formatoSubido.stats.estimatedPromptTokens}</strong> tokens</span>
                </div>

                {formatoSubido.sections.length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Secciones detectadas</div>
                    <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#4b5563' }}>
                      {formatoSubido.sections.map(s => <li key={s.order}>{s.title}</li>)}
                    </ol>
                  </div>
                )}

                {formatoSubido.placeholders.length > 0 ? (
                  <div style={{ marginTop: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Placeholders a rellenar</div>
                    <div>{formatoSubido.placeholders.map(p => <span key={p.token} style={S.chip}>{p.token}</span>)}</div>
                  </div>
                ) : (
                  <div style={{ ...aviso('atencion'), marginTop: 14 }}>
                    No se detectaron placeholders. El modelo rellenará el documento guiándose solo por las secciones.
                  </div>
                )}

                {userId && !bdCaida && (
                  <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                      Guardar para reutilizarlo
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        style={{ ...S.input, flex: 1 }}
                        value={nombreNuevo}
                        onChange={(e) => setNombreNuevo(e.target.value)}
                        placeholder="Nombre del formato"
                      />
                      <button
                        style={{ ...S.btn(guardandoFormato), padding: '9px 18px', fontSize: 14 }}
                        onClick={guardarParaReutilizar}
                        disabled={guardandoFormato}
                      >
                        {guardandoFormato ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 6 }}>
                      Opcional: puedes generar sin guardarlo.
                    </div>
                  </div>
                )}
              </>
            )}

            <input
              ref={inputFormato} type="file" accept=".docx" style={{ display: 'none' }}
              onChange={(e) => subirFormato(e.target.files && e.target.files[0])}
            />
          </>
        )}

        {pestana === 'ninguno' && (
          <div style={{ fontSize: 14, color: '#4b5563' }}>
            Se generará una minuta con estructura estándar: datos de la reunión, asistentes,
            temas, acuerdos, tareas y próximos pasos.
          </div>
        )}
      </div>

      {/* ── 2. Transcripción ── */}
      <div style={S.card}>
        <h2 style={S.cardTitle}><span style={S.step}>2</span> Transcripción</h2>

        <textarea
          style={S.textarea}
          value={transcripcion}
          onChange={(e) => setTranscripcion(e.target.value)}
          placeholder={'Pega aquí la transcripción.\n\nSe admite texto plano ("Ana: ...") o el JSON de Teams.'}
        />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          <button style={S.btnSec} onClick={() => inputTranscripcion.current && inputTranscripcion.current.click()}>
            Cargar desde archivo (.vtt, .srt, .txt, .json)
          </button>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>
            {transcripcion.length.toLocaleString('es')} caracteres
            {transcripcion.length > 60000 && ' · se condensará antes de generar'}
          </span>
        </div>

        <input
          ref={inputTranscripcion} type="file" accept=".vtt,.srt,.txt,.json" style={{ display: 'none' }}
          onChange={(e) => cargarTranscripcion(e.target.files && e.target.files[0])}
        />
      </div>

      {/* ── 3. Indicaciones ── */}
      <div style={S.card}>
        <h2 style={S.cardTitle}>
          <span style={S.step}>3</span> Indicaciones
          <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 13 }}>(opcional)</span>
        </h2>
        <input
          style={S.input}
          value={indicaciones}
          onChange={(e) => setIndicaciones(e.target.value)}
          placeholder="Ej.: destaca los compromisos con fecha; tono formal"
        />
        {formatoElegido && formatoElegido.instructions && !indicaciones.trim() && (
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
            Sin escribir nada se usarán las del formato: «{formatoElegido.instructions}»
          </div>
        )}
      </div>

      <button style={S.btn(!puedeGenerar)} onClick={generar} disabled={!puedeGenerar}>
        {generando ? 'Generando minuta…' : 'Generar minuta'}
      </button>

      {/* ── Resultado ── */}
      {resultado && (
        <div style={{ ...S.card, marginTop: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h2 style={{ ...S.cardTitle, margin: 0 }}>Minuta generada</h2>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={descargarWord}
                disabled={exportando}
                style={{ ...S.btn(exportando), padding: '8px 16px', fontSize: 14 }}
              >
                {exportando ? 'Generando Word…' : 'Descargar Word'}
              </button>
              <button onClick={descargarHtml} style={{ ...S.btnSec, padding: '8px 14px' }}>
                HTML
              </button>
            </div>
          </div>

          {v && !v.ok && (
            <div style={aviso('atencion')}>
              <strong>Revisa el resultado:</strong>
              <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
                {v.issues.map((p, i) => <li key={i}>{p}</li>)}
              </ul>
            </div>
          )}
          {v && v.ok && v.sections && (
            <div style={aviso('exito')}>
              El resultado conserva las {v.sections.expected} secciones del formato
              {v.placeholders ? ` y sustituye sus ${v.placeholders.expected} placeholders` : ''}.
            </div>
          )}

          <div style={S.preview} dangerouslySetInnerHTML={{ __html: resultado.html }} />

          <div style={S.meta}>
            <span>{resultado.meta.elapsedMs} ms</span>
            <span>{resultado.uso.totalTokens.toLocaleString('es')} tokens</span>
            <span>deployment: {resultado.meta.deployment}</span>
            {resultado.meta.condensed && <span>transcripción condensada</span>}
            {resultado.meta.speakers.length > 0 && <span>{resultado.meta.speakers.length} participantes</span>}
          </div>
        </div>
      )}
    </div>
  );
};

export default GeneradorMinutaV2;
